/**
 * Shared microcopy + step validation for Nova Loja (page + drawer).
 */

import {
  isValidCep,
  isValidCnpj,
  isValidEmail,
  isValidPhone,
  isValidUf,
  maskCep,
  maskCnpj,
  maskPhone,
  maskUf,
} from './brazilian-format.js';

export const NEW_STORE_COPY = {
  name: { label: 'Nome fantasia', placeholder: 'Ex.: CasaMax' },
  fiscal_name: { label: 'Razão social', placeholder: 'Ex.: CasaMax Comércio Ltda' },
  domain: { label: 'Domínio', placeholder: 'casamax' },
  email: {
    label: 'E-mail do admin',
    placeholder: 'admin@empresa.com.br',
    hint: 'Usado para login no painel da loja e envio de faturas.',
  },
  password: { label: 'Senha', placeholder: 'Mínimo 8 caracteres' },
  confirmPass: { label: 'Confirmar senha', placeholder: 'Repita a senha' },
  cnpj: { label: 'CNPJ', placeholder: '00.000.000/0000-00' },
  phone: { label: 'Telefone', placeholder: '(11) 99999-9999' },
  contact_email: { label: 'E-mail de contato', placeholder: 'contato@empresa.com.br' },
  postal_code: { label: 'CEP', placeholder: '00000-000' },
  street: { label: 'Logradouro', placeholder: 'Rua, avenida ou rodovia' },
  number: { label: 'Número', placeholder: '123' },
  complement: { label: 'Complemento', placeholder: 'Sala, andar…' },
  neighborhood: { label: 'Bairro', placeholder: 'Centro' },
  city: { label: 'Cidade', placeholder: 'São Paulo' },
  state: { label: 'UF', placeholder: 'SP' },
  notification_sender_email: {
    label: 'E-mail remetente',
    placeholder: 'notificacoes@empresa.com.br',
  },
  notification_sender_name: {
    label: 'Nome remetente',
    placeholder: 'Nome da loja',
  },
  dc_name: { label: 'Nome do CD', placeholder: 'CD Matriz' },
  dc_email: { label: 'E-mail', placeholder: 'logistica@empresa.com.br' },
  dc_phone: { label: 'Telefone', placeholder: '(11) 99999-9999' },
  dc_postcode: { label: 'CEP', placeholder: '00000-000' },
  dc_street: { label: 'Logradouro', placeholder: 'Rua, avenida ou rodovia' },
  dc_number: { label: 'Número', placeholder: '123' },
  dc_complement: { label: 'Complemento', placeholder: 'Sala, andar…' },
  dc_neighborhood: { label: 'Bairro', placeholder: 'Distrito Industrial' },
  dc_city: { label: 'Cidade', placeholder: 'São Paulo' },
  dc_state: { label: 'UF', placeholder: 'SP' },
};

// Keep in sync with saas/core/tenant_domain.py RESERVED_SUBDOMAINS.
export const RESERVED_SUBDOMAINS = Object.freeze([
  'www',
  'api',
  'admin',
  'manage',
  'app',
  'static',
  'media',
  'mail',
  'ftp',
  'localhost',
  'super-admin',
  'superadmin',
  'dashboard',
  'checkout',
  'graphql',
]);

// Keep in sync with saas/core/tenant_domain.py SUBDOMAIN_PATTERN.
const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$|^[a-z0-9]$/;

const MASKERS = {
  cnpj: maskCnpj,
  phone: maskPhone,
  postal_code: maskCep,
  state: maskUf,
  dc_phone: maskPhone,
  dc_postcode: maskCep,
  dc_state: maskUf,
};

export function applyFieldMask(field, value) {
  const masker = MASKERS[field];
  return masker ? masker(value) : value;
}

export function isValidSubdomainFormat(value) {
  const subdomain = String(value || '').trim().toLowerCase();
  if (!subdomain) return false;
  if (subdomain.includes('://') || subdomain.includes('/') || subdomain.includes(' ')) return false;
  return SUBDOMAIN_PATTERN.test(subdomain);
}

export function isReservedSubdomain(value) {
  const subdomain = String(value || '').trim().toLowerCase();
  return RESERVED_SUBDOMAINS.includes(subdomain);
}

export function validateDomainField(value) {
  const subdomain = String(value || '').trim();
  if (!subdomain) return 'Obrigatório';
  if (!isValidSubdomainFormat(subdomain)) {
    return 'Apenas letras minúsculas, números e hífens (não pode começar ou terminar com hífen)';
  }
  if (isReservedSubdomain(subdomain)) return 'Este subdomínio é reservado';
  return null;
}

/** Human messages for GET /tenants/domain-check/ reasons. */
export function domainCheckFeedback(result) {
  if (!result) return null;
  if (result.available) {
    return { tone: 'ok', text: 'Domínio disponível' };
  }
  if (result.reason === 'reserved') {
    return { tone: 'error', text: 'Este subdomínio é reservado' };
  }
  if (result.reason === 'taken') {
    return { tone: 'error', text: 'Este domínio já está em uso' };
  }
  return { tone: 'error', text: 'Domínio indisponível' };
}

export function validateNewStoreStep(step, form) {
  const e = {};

  if (step === 1) {
    if (!form.name.trim()) e.name = 'Obrigatório';
    if (!form.fiscal_name.trim()) e.fiscal_name = 'Obrigatório';
    const domainError = validateDomainField(form.domain);
    if (domainError) e.domain = domainError;
  }

  if (step === 2) {
    if (!form.email.trim()) e.email = 'Obrigatório';
    else if (!isValidEmail(form.email)) e.email = 'E-mail inválido';
    if (!form.send_invite_email) {
      if (!form.password) e.password = 'Obrigatório';
      else if (form.password.length < 8) e.password = 'Mínimo 8 caracteres';
      if (form.password !== form.confirmPass) e.confirmPass = 'Senhas não coincidem';
    }
  }

  if (step === 3) {
    if (!form.cnpj.trim()) e.cnpj = 'Obrigatório';
    else if (!isValidCnpj(form.cnpj)) e.cnpj = 'CNPJ inválido';

    if (!form.phone.trim()) e.phone = 'Obrigatório';
    else if (!isValidPhone(form.phone)) e.phone = 'Telefone inválido';

    if (!form.contact_email.trim()) e.contact_email = 'Obrigatório';
    else if (!isValidEmail(form.contact_email)) e.contact_email = 'E-mail inválido';

    if (!form.postal_code.trim()) e.postal_code = 'Obrigatório';
    else if (!isValidCep(form.postal_code)) e.postal_code = 'CEP inválido';

    if (!form.street.trim()) e.street = 'Obrigatório';
    if (!form.city.trim()) e.city = 'Obrigatório';

    if (!form.state.trim()) e.state = 'Obrigatório';
    else if (!isValidUf(form.state)) e.state = 'UF inválida';

    if (form.notification_sender_email.trim() && !isValidEmail(form.notification_sender_email)) {
      e.notification_sender_email = 'E-mail inválido';
    }
  }

  if (step === 4) {
    if (!form.dc_name.trim()) e.dc_name = 'Obrigatório';
    if (!form.dc_same_as_store) {
      if (!form.dc_email.trim()) e.dc_email = 'Obrigatório';
      else if (!isValidEmail(form.dc_email)) e.dc_email = 'E-mail inválido';

      if (form.dc_phone.trim() && !isValidPhone(form.dc_phone)) e.dc_phone = 'Telefone inválido';

      if (form.dc_postcode.trim() && !isValidCep(form.dc_postcode)) e.dc_postcode = 'CEP inválido';

      if (!form.dc_street.trim()) e.dc_street = 'Obrigatório';
      if (!form.dc_city.trim()) e.dc_city = 'Obrigatório';

      if (form.dc_state.trim() && !isValidUf(form.dc_state)) e.dc_state = 'UF inválida';
    }
  }

  return e;
}
